import type { DocPackPlaybackAnchor } from '@listening/frontend/audio-chunk-player/core';
import { docPackAnchorsEqual } from '@listening/frontend/doc-pack/text-layout';
import type {
  FollowAnchor,
  SyncReadAlongFollowAdapter,
} from '@listening/frontend/read-along/follow-controller';
import type {
  FollowBand,
  ScrollTarget,
} from '@listening/frontend/read-along/follow-machine';
import {
  tanstackScrollToIndex,
  type useVirtual,
} from '@/lib/doc-reader/core-tanstack';

/**
 * Web text adapters for the shared read-along follow engine — the impure
 * 10%: one batched DOM read per controller tick (the active highlight
 * element's rect) and scrollTo execution. All scroll DECISIONS live in the
 * shared pure machine, and the shared controller owns settle detection (it
 * polls getViewport), so there is no timer or listener bookkeeping here.
 *
 * Rest placement: the active word/paragraph sits at 15% of the viewport; the
 * comfort band lets it drift down to 70% before one calm scroll restores it.
 */
const WEB_TEXT_REST_FRACTION = 0.15;
export const WEB_TEXT_FOLLOW_BAND: FollowBand = { up: 0, down: 0.55 };

/** Matches the old IntersectionObserver rootMargin (play bar overlap). */
const VISIBILITY_BOTTOM_MARGIN_PX = 72;

/** Shared shape: resolve the anchor to its live highlight element when the
 * row is mounted, or a coarse virtualizer index to jump to while it isn't. */
type WebTextAnchorResolution = {
  readonly element: HTMLElement | null;
  readonly coarseIndex: number | undefined;
};

function createWebVirtualTextFollowAdapter<A extends FollowAnchor>({
  getAnchor,
  anchorsEqual,
  resolveAnchor,
  getScrollElement,
  getVirtualizer,
  onActiveVisibleChange,
}: {
  readonly getAnchor: () => A | undefined;
  readonly anchorsEqual: (a: A, b: A) => boolean;
  readonly resolveAnchor: (anchor: A) => WebTextAnchorResolution;
  readonly getScrollElement: () => HTMLDivElement | null;
  readonly getVirtualizer: () => ReturnType<typeof useVirtual> | null;
  /** Feeds the existing jump-to-current button state. */
  readonly onActiveVisibleChange?: ((visible: boolean) => void) | undefined;
}): SyncReadAlongFollowAdapter<A> {
  let lastReportedVisible: boolean | undefined;

  const liveElement = (anchor: A): HTMLElement | null => {
    const element = resolveAnchor(anchor).element;
    return element?.isConnected ? element : null;
  };

  return {
    getAnchor,
    anchorsEqual,

    resolveTarget: (anchor): ScrollTarget | null => {
      const container = getScrollElement();
      if (!container) return null;
      const { element, coarseIndex } = resolveAnchor(anchor);
      if (!element?.isConnected)
        return coarseIndex === undefined
          ? null
          : { kind: 'page', index: coarseIndex };
      const containerRect = container.getBoundingClientRect();
      const elementY =
        element.getBoundingClientRect().top -
        containerRect.top +
        container.scrollTop;
      const rest = WEB_TEXT_REST_FRACTION * container.clientHeight;
      const maxScroll = Math.max(
        0,
        container.scrollHeight - container.clientHeight,
      );
      return {
        kind: 'offset',
        y: Math.min(maxScroll, Math.max(0, elementY - rest)),
      };
    },

    getViewport: () => {
      const container = getScrollElement();
      return {
        scrollY: container?.scrollTop ?? 0,
        height: container?.clientHeight ?? 0,
      };
    },

    isActiveVisible: (anchor) => {
      const container = getScrollElement();
      const element = anchor === undefined ? null : liveElement(anchor);
      let visible = false;
      if (container && element) {
        const containerRect = container.getBoundingClientRect();
        const rect = element.getBoundingClientRect();
        visible =
          rect.bottom > containerRect.top &&
          rect.top < containerRect.bottom - VISIBILITY_BOTTOM_MARGIN_PX;
      }
      if (visible !== lastReportedVisible) {
        lastReportedVisible = visible;
        onActiveVisibleChange?.(visible);
      }
      return visible;
    },

    executeScroll: (target, { animated }) => {
      const container = getScrollElement();
      if (!container) return;
      if (target.kind === 'page') {
        const virtualizer = getVirtualizer();
        if (virtualizer) tanstackScrollToIndex(virtualizer, target.index);
        return;
      }
      container.scrollTo({
        top: target.y,
        behavior: animated ? 'smooth' : 'auto',
      });
    },

    cancelScroll: () => {
      const container = getScrollElement();
      // An instant scroll to the current position halts a smooth animation.
      container?.scrollTo({ top: container.scrollTop, behavior: 'auto' });
    },
  };
}

/** USER-gesture handlers for the scroll area root — deliberately NOT `scroll`
 * events (those can't distinguish the user from programmatic/virtualizer
 * scrolls). Pointer-down only counts outside the viewport (the scrollbar);
 * inside it is content interaction (word taps). */
export function webFollowGestureProps({
  getScrollElement,
  onUserGesture,
}: {
  readonly getScrollElement: () => HTMLDivElement | null;
  readonly onUserGesture: () => void;
}) {
  return {
    onWheel: onUserGesture,
    onTouchMove: onUserGesture,
    onPointerDown: (event: React.PointerEvent) => {
      const viewport = getScrollElement();
      if (
        viewport &&
        event.target instanceof Node &&
        viewport.contains(event.target)
      )
        return;
      onUserGesture();
    },
    onKeyDown: (event: React.KeyboardEvent) => {
      if (
        ['ArrowDown', 'ArrowUp', 'End', 'Home', 'PageDown', 'PageUp'].includes(
          event.key,
        )
      )
        onUserGesture();
    },
  };
}

// ---------------------------------------------------------------------------
// doc-pack Text View: anchor = the doc-pack word anchor; the highlight span
// is stamped `data-doc-pack-active` by the 20fps highlight render.

export function createDocPackTextFollowAdapter({
  getAnchor,
  getScrollElement,
  getVirtualizer,
  getPageIndex,
  onActiveVisibleChange,
}: {
  readonly getAnchor: () => DocPackPlaybackAnchor | undefined;
  readonly getScrollElement: () => HTMLDivElement | null;
  readonly getVirtualizer: () => ReturnType<typeof useVirtual> | null;
  readonly getPageIndex: (pageNumber: number) => number | undefined;
  readonly onActiveVisibleChange?: (visible: boolean) => void;
}): SyncReadAlongFollowAdapter<DocPackPlaybackAnchor> {
  return createWebVirtualTextFollowAdapter<DocPackPlaybackAnchor>({
    getAnchor,
    anchorsEqual: docPackAnchorsEqual,
    resolveAnchor: (anchor) => {
      const container = getScrollElement();
      const span = container?.querySelector<HTMLElement>(
        '[data-doc-pack-active]',
      );
      // The span is stamped by the highlight render, which samples playback
      // independently of the controller — only trust it for the same page
      // (word-level lag within a page is harmless for scroll math).
      return {
        element:
          span && Number(span.dataset.docPackPage) === anchor.page
            ? span
            : null,
        coarseIndex: getPageIndex(anchor.page),
      };
    },
    getScrollElement,
    getVirtualizer,
    onActiveVisibleChange,
  });
}

// ---------------------------------------------------------------------------
// Legacy (chapter_details) text view: anchor = the active paragraph ELEMENT
// while its row is mounted (identity equality — React re-points the ref as
// the highlight crosses paragraphs), falling back to a ttsId pseudo-anchor
// that resolves to a coarse virtualizer jump when the row isn't rendered
// (e.g. right after a seek into an unloaded chapter).

export type LegacyTextFollowAnchor = HTMLElement | { readonly ttsId: string };

// ---------------------------------------------------------------------------
// PDF view (legacy AND doc-pack): one adapter for both content sources —
// `useWordHighlightManager` maintains the active word/sentence bbox element
// for both, so the anchor is that element while it's mounted, falling back to
// a page-level pseudo-anchor (from the doc-pack playback anchor or the
// chunk's first bbox page) when the page isn't rendered.

export type PdfFollowAnchor =
  | HTMLElement
  | { readonly key: string; readonly pageIndex: number | null };

export function createPdfFollowAdapter({
  getActiveElement,
  getFallbackAnchor,
  getScrollElement,
  getVirtualizer,
  onActiveVisibleChange,
}: {
  readonly getActiveElement: () => HTMLElement | null;
  /** Page-level pseudo-anchor while the highlight element isn't mounted. */
  readonly getFallbackAnchor: () =>
    | { readonly key: string; readonly pageIndex: number | null }
    | undefined;
  readonly getScrollElement: () => HTMLDivElement | null;
  readonly getVirtualizer: () => ReturnType<typeof useVirtual> | null;
  readonly onActiveVisibleChange?: (visible: boolean) => void;
}): SyncReadAlongFollowAdapter<PdfFollowAnchor> {
  return createWebVirtualTextFollowAdapter<PdfFollowAnchor>({
    getAnchor: () => {
      const element = getActiveElement();
      if (element?.isConnected) return element;
      return getFallbackAnchor();
    },
    anchorsEqual: (a, b) =>
      a instanceof HTMLElement || b instanceof HTMLElement
        ? a === b
        : a.key === b.key,
    resolveAnchor: (anchor) =>
      anchor instanceof HTMLElement
        ? { element: anchor, coarseIndex: undefined }
        : { element: null, coarseIndex: anchor.pageIndex ?? undefined },
    getScrollElement,
    getVirtualizer,
    onActiveVisibleChange,
  });
}

export function createLegacyTextFollowAdapter({
  getActiveElement,
  getTtsId,
  findScrollableIndexOfTtsChunk,
  getScrollElement,
  getVirtualizer,
  onActiveVisibleChange,
}: {
  readonly getActiveElement: () => HTMLElement | null;
  readonly getTtsId: () => string | undefined;
  /** Sync lookup; may point at the chapter header while its chunk loads. */
  readonly findScrollableIndexOfTtsChunk: (ttsId: string) => number | null;
  readonly getScrollElement: () => HTMLDivElement | null;
  readonly getVirtualizer: () => ReturnType<typeof useVirtual> | null;
  readonly onActiveVisibleChange?: (visible: boolean) => void;
}): SyncReadAlongFollowAdapter<LegacyTextFollowAnchor> {
  return createWebVirtualTextFollowAdapter<LegacyTextFollowAnchor>({
    getAnchor: () => {
      const element = getActiveElement();
      if (element?.isConnected) return element;
      const ttsId = getTtsId();
      return ttsId === undefined ? undefined : { ttsId };
    },
    anchorsEqual: (a, b) =>
      a instanceof HTMLElement || b instanceof HTMLElement
        ? a === b
        : a.ttsId === b.ttsId,
    resolveAnchor: (anchor) => {
      if (anchor instanceof HTMLElement)
        return { element: anchor, coarseIndex: undefined };
      const index = findScrollableIndexOfTtsChunk(anchor.ttsId);
      return { element: null, coarseIndex: index ?? undefined };
    },
    getScrollElement,
    getVirtualizer,
    onActiveVisibleChange,
  });
}
